/* eslint react-hooks/exhaustive-deps: "warn" */

import { useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './IncludedSection.module.scss';

type Translate = (key: string) => string;

type Kpi = {
  label: string;
  value: string;
  delta?: string;
  official?: boolean;
  accent?: boolean;
};

const getKpis = (t: Translate): Kpi[] => [
  { label: t('Revenue'), value: '$130.5B', delta: '↑ 114.2%', official: true, accent: true },
  { label: t('Net income'), value: '$72.9B', delta: '↑ 144.9%', official: true, accent: true },
  { label: t('Headcount'), value: '36,000', delta: '↑ 21.6%', official: true, accent: true },
  { label: t('Fiscal year'), value: 'FY2025', delta: t('ended 26 Jan 2025') },
];

const getRegistry = (t: Translate): Array<{ label: string; value: string }> => [
  { label: t('Registration number'), value: 'CA 2862596' },
  { label: t('Incorporated'), value: '5 Apr 1993' },
  { label: t('Registered address'), value: '2788 San Tomas Expressway, Santa Clara, CA 95051' },
  { label: t('Country of origin'), value: `🇺🇸 ${t('United States')}` },
  { label: t('Legal form'), value: t('Corporation') },
];

export const IncludedSection = () => {
  const { t } = useInt();
  const kpis = getKpis(t);
  const registry = getRegistry(t);
  const revealRef = useRef<HTMLDivElement>(null);
  const dossierRevealed = useIntersection(revealRef, { threshold: 0.15 });
  const dossierRef = useRef<HTMLDivElement>(null);

  const handleCardMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = dossierRef.current;

    if (!card || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const rect = card.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    const maxTilt = 2.5;

    card.style.transform = `perspective(1400px) rotateX(${(-py * maxTilt).toFixed(2)}deg) rotateY(${(px * maxTilt).toFixed(2)}deg) translateY(-4px)`;
    card.style.setProperty('--glareX', `${(px + 0.5) * 100}%`);
    card.style.setProperty('--glareY', `${(py + 0.5) * 100}%`);
  };

  const handleCardMouseLeave = () => {
    const card = dossierRef.current;

    if (!card) {
      return;
    }

    card.style.transform = '';
  };

  return (
    <section className={styles.root}>
      <div className={styles.container}>
        <div className={styles.head}>
          <TitleBlock
            theme="dark"
            title={t('Every entity resolves to a full company record')}
            description={t(
              'Linkage connects the entities — each one carries its complete profile, joined on the same persistent ID.',
            )}
          />
        </div>

        <div className={cn(styles.dossierWrap, dossierRevealed && styles.dossierWrapIn)} ref={revealRef}>
          <div
            className={styles.dossier}
            ref={dossierRef}
            onMouseMove={handleCardMouseMove}
            onMouseLeave={handleCardMouseLeave}
          >
            <div className={styles.dosGlare} aria-hidden="true" />

            <div className={styles.dosSide}>
              <div>
                <div className={styles.dosName}>NVIDIA Corporation</div>
                <span className={styles.dosActive}>
                  <span className={styles.dosActiveDot}>
                    <span className={styles.dosActivePing} />
                  </span>
                  {t('Active')}
                </span>
                <div className={styles.dosForm}>{t('Corporation')} · NASDAQ: NVDA</div>
                <div className={styles.dosSub}>{t('GLOBAL ULTIMATE')} · US CA 2862596</div>
              </div>

              <div className={styles.dosFoot}>
                <span className={styles.dosFootLabel}>{t('Also on this record')}</span>
                <span className={styles.dosFootList}>
                  {t('Financials · Credit risk · Employees · Ownership · Digital insights · News · M&A activity')}
                </span>
              </div>
            </div>

            <div className={styles.dosMain}>
              <div className={styles.kpis}>
                {kpis.map((kpi) => (
                  <div key={kpi.label} className={cn(styles.kpi, kpi.accent && styles.kpiAccent)}>
                    <div className={styles.kpiLabel}>
                      {kpi.label}
                      {kpi.official && <span className={styles.kpiBadge}>{t('Official')}</span>}
                    </div>
                    <div className={styles.kpiValue}>{kpi.value}</div>
                    {kpi.delta && (
                      <div className={cn(styles.kpiDelta, kpi.official && styles.kpiUp)}>{kpi.delta}</div>
                    )}
                  </div>
                ))}
              </div>

              <div className={styles.regPanel}>
                <div className={styles.regHead}>
                  <span>{t('Official registry data')}</span>
                  <span className={styles.regBadge}>✓ {t('Government registry')}</span>
                </div>
                {registry.map((row) => (
                  <div key={row.label} className={styles.regRow}>
                    <span className={styles.regLabel}>{row.label}</span>
                    <span className={styles.regValue}>{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
